import {
    useId,
    useRef,
    type CSSProperties,
    type FormEvent,
    type KeyboardEvent,
    type PointerEvent,
    type ReactElement,
    type ReactNode,
    type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
    AnimatePresence,
    motion,
    type MotionStyle,
    type TargetAndTransition,
    type Transition,
} from "framer-motion";
import { X } from "lucide-react";
import {
    color,
    composeTransform,
    elevation,
    motion as motionTokens,
    presenceLayerStyle,
    radius,
    REST_TRANSFORM,
    space,
    useOverlayMotion,
    usePrefersReducedMotion,
    useScrimMotion,
} from "@tokens/index";
import { IconButton } from "../IconButton";
import { motionStyle } from "../motionStyle";
import { Text } from "../Text";
import { useViewport } from "../viewport";
import { useEscapeKey } from "./useEscapeKey";
import { useFocusTrap } from "./useFocusTrap";
import { useModalLock } from "./useModalLock";
import { DIALOG_Z_INDEX } from "./zIndex";

/**
 * Dialog: the only modal container.
 *
 * Owns portal, scrim, focus trap, focus return, Escape, scrim click, body
 * scroll lock, and `aria-modal` wiring. Owns its `AnimatePresence`; callers
 * toggle `open` and do not wrap the dialog in a presence boundary.
 *
 * `presentation="auto"` renders a centered card on `medium` and wider, and a
 * bottom sheet on `compact`.
 *
 * `fill` fixes the card height at the viewport cap so a child with
 * `height: 100%` resolves. `bodyPadding="none"` lets that child own the
 * full card width.
 *
 * `as="form"` renders the card as a `<form>` so the header, body, and footer
 * share one form: Enter in any field and a footer `Button type="submit"`
 * both reach `onSubmit`. Textareas submit on Cmd/Ctrl+Enter through
 * `useSubmitShortcut`.
 *
 * Focus on open: `initialFocusRef`, else the first `[data-autofocus]`, else
 * the first form control, else the card.
 */

export type DialogSize = "sm" | "md" | "lg" | "xl";
export type DialogPresentation = "auto" | "center" | "sheet";
export type DialogBodyPadding = "default" | "none";
export type DialogElement = "div" | "form";

export interface DialogProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    description?: string;
    icon?: ReactNode;
    size?: DialogSize;
    presentation?: DialogPresentation;
    /** Default `"default"`. */
    bodyPadding?: DialogBodyPadding;
    /** Fix the card height at the viewport cap. Default `false`. */
    fill?: boolean;
    /** Element to focus on open. See the focus order above. */
    initialFocusRef?: RefObject<HTMLElement | null>;
    /** Render the card as this element. Default `"div"`. */
    as?: DialogElement;
    /** Submit handler when `as="form"`. `preventDefault` is called for you. */
    onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
    footer?: ReactNode;
    headerActions?: ReactNode;
    /** Hide the header row. `title` still labels the dialog for assistive tech. */
    hideHeader?: boolean;
    /** Default `true`. */
    closeOnScrimClick?: boolean;
    /** Default `true`. */
    closeOnEscape?: boolean;
    /** Id of an external element that labels the dialog. Overrides `title`. */
    labelledBy?: string | undefined;
    children: ReactNode;
    testId?: string | undefined;
}

const sizeMaxWidth: Record<DialogSize, number> = {
    sm: 400,
    md: 560,
    lg: 640,
    xl: 960,
};

const rootStyle = (sheet: boolean): CSSProperties => ({
    position: "fixed",
    inset: 0,
    zIndex: DIALOG_Z_INDEX,
    display: "flex",
    alignItems: sheet ? "flex-end" : "center",
    justifyContent: "center",
    padding: sheet ? 0 : space[5],
});

// Scrim and card each hold their compositor layer for the dialog's lifetime;
// see `presenceLayerStyle`.
const scrimStyle: MotionStyle = {
    position: "absolute",
    inset: 0,
    background: color.effect.scrim,
    ...presenceLayerStyle,
};

const cardBaseStyle: CSSProperties = {
    ...presenceLayerStyle,
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: 0,
    margin: 0,
    background: color.bg.surface,
    color: color.fg.default,
    border: `1px solid ${color.border.subtle}`,
    overflow: "hidden",
    outline: "none",
    transformOrigin: "50% 50%",
};

const SHEET_RISE_PX = 24;
const SHEET_MAX_HEIGHT = "calc(100dvh - env(safe-area-inset-top, 0px) - 24px)";
const CARD_MAX_HEIGHT = "calc(100dvh - 48px)";

const cardRadius = (sheet: boolean): string =>
    sheet ? `${radius.xl} ${radius.xl} 0 0` : radius.xl;

// Elevation travels with the card: opacity + transform are compositor-only,
// so the shadow fades and scales as one texture instead of popping in after
// enter or snapping off before exit.
function cardStyle(size: DialogSize, sheet: boolean, fill: boolean): CSSProperties {
    if (sheet) {
        return {
            ...cardBaseStyle,
            maxWidth: "100%",
            maxHeight: SHEET_MAX_HEIGHT,
            height: fill ? SHEET_MAX_HEIGHT : undefined,
            borderRadius: cardRadius(true),
            borderBottom: "none",
            boxShadow: elevation[4].shadow,
        };
    }
    return {
        ...cardBaseStyle,
        maxWidth: sizeMaxWidth[size],
        maxHeight: CARD_MAX_HEIGHT,
        height: fill ? CARD_MAX_HEIGHT : undefined,
        borderRadius: cardRadius(false),
        boxShadow: elevation[3].shadow,
    };
}

const headerStyle: CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    gap: space[3],
    padding: `${space[4]} ${space[4]} ${space[3]} ${space[5]}`,
    flexShrink: 0,
};

const bodyPaddingValue: Record<DialogBodyPadding, string | number> = {
    default: `${space[2]} ${space[5]} ${space[5]}`,
    none: 0,
};

function bodyStyle(padding: DialogBodyPadding): CSSProperties {
    return {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overscrollBehavior: "contain",
        padding: bodyPaddingValue[padding],
    };
}

const footerStyle: CSSProperties = {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: space[2],
    padding: `${space[3]} ${space[5]} ${space[4]}`,
    borderTop: `1px solid ${color.border.subtle}`,
    flexShrink: 0,
};

const dragHandleStyle: CSSProperties = {
    width: 36,
    height: 4,
    borderRadius: radius.pill,
    background: color.border.strong,
    margin: `${space[2]} auto 0`,
    flexShrink: 0,
};

interface SheetMotion {
    initial: TargetAndTransition;
    animate: TargetAndTransition;
    exit: TargetAndTransition;
    transition: Transition;
}

function sheetMotion(reduced: boolean): SheetMotion {
    if (reduced) {
        const fade: Transition = { duration: motionTokens.reduced.duration };
        return {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            exit: { opacity: 0, transition: fade },
            transition: fade,
        };
    }
    const { enter, exit } = motionTokens.overlay;
    const from = composeTransform(SHEET_RISE_PX, 1);
    return {
        initial: { opacity: 0, transform: from },
        animate: { opacity: 1, transform: REST_TRANSFORM },
        exit: {
            opacity: 0,
            transform: from,
            transition: { duration: exit.duration, ease: exit.ease },
        },
        transition: { duration: enter.duration, ease: enter.ease },
    };
}

const MotionForm = motion.form;
const MotionDiv = motion.div;

export function Dialog({
    open,
    onClose,
    title,
    description,
    icon,
    size = "md",
    presentation = "auto",
    bodyPadding = "default",
    fill = false,
    initialFocusRef,
    as = "div",
    onSubmit,
    footer,
    headerActions,
    hideHeader = false,
    closeOnScrimClick = true,
    closeOnEscape = true,
    labelledBy,
    children,
    testId,
}: DialogProps): ReactElement | null {
    const { isCompact } = useViewport();
    const sheet = presentation === "sheet" || (presentation === "auto" && isCompact);
    const reduced = usePrefersReducedMotion();
    const overlayMotion = useOverlayMotion("dialog");
    const scrimMotion = useScrimMotion();
    const cardRef = useRef<HTMLDivElement & HTMLFormElement>(null);
    const titleId = useId();
    const descriptionId = useId();

    useModalLock(open);
    useEscapeKey(open && closeOnEscape, onClose);
    const trap = useFocusTrap(cardRef, { active: open, initialFocusRef, returnFocus: true });

    const handleCardKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
        if (event.key === "Escape" && closeOnEscape && !event.isDefaultPrevented()) {
            event.preventDefault();
            onClose();
            return;
        }
        trap.onKeyDown(event);
    };

    const handleScrimPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
        if (!closeOnScrimClick) return;
        if (event.target !== event.currentTarget) return;
        onClose();
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        onSubmit?.(event);
    };

    if (typeof document === "undefined") return null;

    const hasTitle = title !== undefined && title.length > 0;
    const ariaLabelledBy = labelledBy ?? (hasTitle ? titleId : undefined);
    const ariaDescribedBy = description !== undefined && !hideHeader ? descriptionId : undefined;
    const card = sheet ? sheetMotion(reduced) : overlayMotion;
    const cardProps = {
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": ariaLabelledBy,
        "aria-describedby": ariaDescribedBy,
        tabIndex: -1,
        "data-testid": testId,
        "data-sheet": sheet ? "" : undefined,
        "data-size": size,
        "data-fill": fill ? "" : undefined,
        style: motionStyle(cardStyle(size, sheet, fill)),
        initial: card.initial,
        animate: card.animate,
        exit: card.exit,
        transition: card.transition,
        onKeyDown: handleCardKeyDown,
    } as const;

    const cardContent = (
        <>
            {sheet && <div aria-hidden="true" style={dragHandleStyle} />}
            {hideHeader ? (
                hasTitle && labelledBy === undefined ? (
                    <span id={titleId} data-visually-hidden="">
                        {title}
                    </span>
                ) : null
            ) : (
                <header style={headerStyle}>
                    {icon !== undefined && (
                        <span
                            aria-hidden="true"
                            style={{
                                display: "inline-flex",
                                color: color.accent.primary,
                                marginTop: space[1],
                                flexShrink: 0,
                            }}
                        >
                            {icon}
                        </span>
                    )}
                    <div style={{ flex: 1, minWidth: 0, display: "grid", gap: space[1] }}>
                        {hasTitle && (
                            <Text as="h2" id={titleId} role="subheading" truncate>
                                {title}
                            </Text>
                        )}
                        {description !== undefined && (
                            <Text as="p" id={descriptionId} role="bodySm" tone="muted">
                                {description}
                            </Text>
                        )}
                    </div>
                    {headerActions}
                    <IconButton
                        icon={X}
                        aria-label="Close"
                        tooltip="Close"
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                    />
                </header>
            )}
            <div data-dialog-body={bodyPadding} style={bodyStyle(bodyPadding)}>
                {children}
            </div>
            {footer !== undefined && <footer style={footerStyle}>{footer}</footer>}
        </>
    );

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div key="dialog" style={motionStyle(rootStyle(sheet))} data-dialog-root="">
                    <motion.div
                        aria-hidden="true"
                        style={scrimStyle}
                        initial={scrimMotion.initial}
                        animate={scrimMotion.animate}
                        exit={scrimMotion.exit}
                        transition={scrimMotion.transition}
                        onPointerDown={handleScrimPointerDown}
                    />
                    {as === "form" ? (
                        <MotionForm ref={cardRef} {...cardProps} noValidate onSubmit={handleSubmit}>
                            {cardContent}
                        </MotionForm>
                    ) : (
                        <MotionDiv ref={cardRef} {...cardProps}>
                            {cardContent}
                        </MotionDiv>
                    )}
                </motion.div>
            )}
        </AnimatePresence>,
        document.body,
    );
}
